// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useEffect, useMemo, useState, useCallback, memo } from 'react'
import { useDebounce } from 'use-debounce'

// others
import {
  Case,
  CaseStatus,
  Maybe,
  OrderCaseBy,
  useFetchAllMattersWithoutJoinLazyQuery,
  useFetchCaseLazyQuery
} from '../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT, QUERY_PARAMS } from '../../constants'
import { MatterDropdownProps } from '../../interfaceTypes/props'

const MatterDropdown: FC<MatterDropdownProps> = ({
  label,
  required,
  disabled,
  setParams,
  getFilterValues,
  customOnChange,
  sx,
  includeClosedMatters = false,
  userId,
  hideAllOption = false,
  excludeConsultMatters = false
}) => {
  // When closed matters should be included, omit the status filter so the API returns
  // matters of every status (active, completed, archived). Otherwise restrict to active only.
  const statusFilter = includeClosedMatters ? undefined : CaseStatus.Active

  const [cases, setCases] = useState<Maybe<Case>[]>([])
  const [casePage, setCasePage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [caseSearchTerm, setCaseSearchTerm] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedCase, setSelectedCase] = useState<Maybe<Case>>(null)

  const [debouncedCaseSearchTerm] = useDebounce(caseSearchTerm, 500)

  const [fetchCaseById] = useFetchCaseLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const caseData = data?.fetchCase?.case
      if (caseData?.id) setSelectedCase(caseData as Case)
    }
  })

  const [fetchCases, { loading }] = useFetchAllMattersWithoutJoinLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onError() {
      setCases([])
      setHasMorePages(false)
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllMattersWithoutJoin: fetchAllCases } = data

        if (fetchAllCases) {
          const { cases: newCases, pagination } = fetchAllCases
          const { page: currentPageNo, totalPages } = pagination || {}

          const newHasMore = currentPageNo && totalPages ? currentPageNo < totalPages : false

          if (newCases) {
            setCases(prevCases => {
              const updatedCases = casePage === 1 ? newCases : [...(prevCases || []), ...newCases]
              return updatedCases
            })

            setHasMorePages(newHasMore)
          } else {
            setHasMorePages(false)
          }
        }
      }
    }
  })

  useEffect(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.MATTER)
    if (!selectedId || selectedId === QUERY_PARAMS.ALL) {
      setInputValue(hideAllOption ? '' : 'All')
    }
  }, [getFilterValues, hideAllOption])

  useEffect(() => {
    setCases([])
    setCasePage(1)
    setHasMorePages(true)
  }, [debouncedCaseSearchTerm])

  useEffect(() => {
    const searchValue = debouncedCaseSearchTerm.trim()

    hasMorePages &&
      fetchCases({
        variables: {
          casesInput: {
            orderBy: OrderCaseBy.Recent,
            status: statusFilter,
            searchValue: searchValue,
            isConsultCase: excludeConsultMatters ? false : undefined,
            ...(userId && { userId }),
            paginationOptions: {
              limit: PAGE_LIMIT,
              page: casePage
            }
          }
        }
      })
  }, [casePage, debouncedCaseSearchTerm, fetchCases, hasMorePages, statusFilter, userId, excludeConsultMatters])

  const ALL_OPTION = { id: QUERY_PARAMS.ALL, title: 'All' }

  const options = useMemo(() => {
    const caseOptions = cases || []
    return hideAllOption ? caseOptions : [ALL_OPTION, ...caseOptions]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, hideAllOption])

  const value = useMemo(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.MATTER)
    if (selectedId === QUERY_PARAMS.ALL || !selectedId) return hideAllOption ? null : ALL_OPTION

    const selected = cases?.find(c => c?.id === selectedId)
    if (selected) return selected

    if (selectedCase && selectedCase.id === selectedId) return selectedCase

    return { id: selectedId, title: 'Loading...' }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, getFilterValues, selectedCase, hideAllOption])

  const getOptionLabel = (option: Case) => {
    const caseNumber = option?.isConsultMatter ? option.consultMatterNumber : option?.caseNumber
    const title = option?.title || ''

    if (caseNumber) return `(#${caseNumber}) ${title}`

    return title
  }

  useEffect(() => {
    if (caseSearchTerm === '' && inputValue === '') {
      if (value?.id === QUERY_PARAMS.ALL) setInputValue('All')
      else if (value?.title && value.title !== 'Loading...') {
        const caseNumber = getOptionLabel(value as Case)
        if (caseNumber) setInputValue(`${caseNumber} ${value.title}`)
        else setInputValue(value.title)
      }
    }
  }, [value, caseSearchTerm, inputValue])

  // When selectedId is from URL/state but not in the current paginated list, fetch case by id so the dropdown can display it
  useEffect(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.MATTER)
    if (!selectedId || selectedId === QUERY_PARAMS.ALL) {
      setSelectedCase(null)
      return
    }

    const inList = cases?.some(c => c?.id === selectedId)
    const alreadyFetched = selectedCase?.id === selectedId
    if (inList || alreadyFetched) return

    fetchCaseById({
      variables: {
        caseInput: { id: selectedId }
      }
    })
  }, [getFilterValues, cases, selectedCase?.id, fetchCaseById])

  useEffect(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.MATTER)
    const searchedMatter = getFilterValues?.(QUERY_PARAMS.KEYWORD)
    if (selectedId && searchedMatter && selectedId !== QUERY_PARAMS.ALL && cases.length > 0) {
      const selected = cases?.find(c => c?.id === selectedId)
      if (!selected && (!selectedCase || selectedCase.id !== selectedId)) {
        const findCaseInAllPages = async () => {
          let page = 1
          let found = false

          while (!found && page <= PAGE_LIMIT) {
            try {
              const result = await fetchCases({
                variables: {
                  casesInput: {
                    orderBy: OrderCaseBy.Title,
                    status: statusFilter,
                    searchValue: searchedMatter || '',
                    isConsultCase: excludeConsultMatters ? false : undefined,
                    ...(userId && { userId }),
                    paginationOptions: {
                      limit: PAGE_LIMIT,
                      page: page
                    }
                  }
                }
              })

              if (result.data?.fetchAllMattersWithoutJoin?.cases) {
                const foundCase = result.data.fetchAllMattersWithoutJoin.cases.find(c => c?.id === selectedId)
                if (foundCase) {
                  setSelectedCase(foundCase)
                  found = true
                }
              }

              if (
                !result.data?.fetchAllMattersWithoutJoin?.cases ||
                result.data.fetchAllMattersWithoutJoin.cases.length < PAGE_LIMIT
              )
                break // No more pages

              page++
            } catch (error) {
              console.error('Error finding selected case:', error)
              break
            }
          }
        }

        findCaseInAllPages()
      }
    }
  }, [getFilterValues, cases, fetchCases, selectedCase, statusFilter, userId, excludeConsultMatters])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
        hasMorePages &&
        !loading
      ) {
        setCasePage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setCaseSearchTerm(newInputValue)
      setInputValue(newInputValue)
    }
  }, [])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: Maybe<Case> | null) => {
      if (newValue) {
        const matterNo = newValue?.isConsultMatter ? newValue?.consultMatterNumber : newValue?.caseNumber
        customOnChange?.(newValue)
        setParams?.(QUERY_PARAMS.MATTER, newValue.id || QUERY_PARAMS.ALL)
        setParams?.(QUERY_PARAMS.KEYWORD, matterNo || '')

        if (newValue.id !== QUERY_PARAMS.ALL) setSelectedCase(newValue)
        else setSelectedCase(null)

        setCaseSearchTerm('')

        if (newValue.id === QUERY_PARAMS.ALL) setInputValue(hideAllOption ? '' : 'All')
        else {
          const caseNumber = getOptionLabel(newValue)
          if (caseNumber) setInputValue(`${caseNumber} ${newValue.title || ''}`)
          else setInputValue(newValue.title || '')
        }
      } else {
        customOnChange?.(null)
        setParams?.(QUERY_PARAMS.MATTER, QUERY_PARAMS.ALL)
        setParams?.(QUERY_PARAMS.KEYWORD, '')
        setSelectedCase(null)
        setInputValue(hideAllOption ? '' : 'All')
        setCaseSearchTerm('')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, hideAllOption]
  )

  return (
    <Autocomplete
      options={options}
      getOptionLabel={option => {
        if (option?.id === QUERY_PARAMS.ALL) return hideAllOption ? '' : 'All'
        return getOptionLabel(option as Case)
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(event, value: Case | { id: QUERY_PARAMS; title: string } | null, reason, details) => {
        handleChange(event, value as Maybe<Case> | null)
      }}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      disabled={disabled}
      value={value as Case | { id: QUERY_PARAMS; title: string } | null}
      renderInput={params => <TextField {...params} label={label} fullWidth required={required} />}
      ListboxProps={{
        role: 'list-box',
        style: {
          maxHeight: '190px'
        },
        onScroll: handleScroll
      }}
      sx={sx ?? { width: '100%' }}
      noOptionsText={loading ? 'Loading...' : cases?.length === 0 ? 'No Matters' : 'No options'}
      loading={loading}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      disableCloseOnSelect={false}
      clearText="Clear"
      clearOnEscape
      disableClearable={false}
    />
  )
}

export default memo(MatterDropdown)
